import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';

export interface TextFilter {
  type: 'text';
}

export interface NumberFilter {
  type: 'number';
  options?: string[];
  unit?: string;
}

export interface DateFilter {
  type: 'date' | 'datetime' | 'time';
}

export interface AmountOfTimeFilter {
  type: 'amount-of-time';
  options?: string[];
}

export interface SelectFilterItem {
  icon?: string;
  matsymbol?: { symbol: string; style?: string };
  color?: string;
  name: string;
  id: string | number;
}

export interface SelectFilterGroup extends SelectFilterItem {
  items: (number | string)[];
}

export interface SelectFilter {
  type: 'select';
  groups?: SelectFilterGroup[];
  items?: SelectFilterItem[];
  options?: string[];
  title?: string;
}

export interface FilterTemplate {
  id: number;
  name: string;
  filters: Record<string, TableFilterValue | null>;
}

/** A filter template as it is stored by / received from the backend. */
export interface TypedFilterTemplate extends Omit<FilterTemplate, 'filters'> {
  filters: Record<string, TypedTableFilterValue | null>;
}

export interface TextFilterValue {
  isEnabled?: boolean;
  mode: FilterModes;
  value: string;
}

export interface NumberFilterIntervalValue {
  method: PeriodFilterMethods;
  number1: number;
  number2: number;
}

export interface NumberFilterRelativeValue {
  method: NumericRelativeFilterMethods;
  number: number;
}

export interface NumberFilterValue {
  isEnabled?: boolean;
  mode: NumberFilterModes;
  value: NumberFilterRelativeValue | NumberFilterIntervalValue;
}

export interface SelectFilterValue {
  isEnabled?: boolean;
  mode: FilterModes;
  method: SelectFilterMethods;
  value: Array<string | number>;
}

export interface DateFilterValue {
  isEnabled?: boolean;
  mode: DateFilterModes;
  value:
    DateFilterAbsoluteValue | DateFilterPeriodValue | DateFilterRelativeValue;
}

export interface DateFilterAbsoluteValue {
  method: AbsoluteFilterMethods;
  value: string;
  time?: string | null;
  /**
   * The measured wire echoes `within: null` when the method takes none, but
   * this stays non-nullable: the type is also what `PeriodSelectorComponent`
   * hands the typed form's `patchValue`, whose `within` group accepts
   * `undefined` and not `null` (at runtime a `null` patch is a no-op). Widening
   * it would move the wire pins' `null as never` into production code.
   */
  within?: { value: number; unit: FilterUnits };
}

export interface DateFilterPeriodValue {
  method: PeriodFilterMethods;
  value1: string;
  time1?: string | null;
  value2: string;
  time2?: string | null;
}

export interface DateFilterRelativeValue {
  method: RelativeFilterMethods;
  value: number;
  unit: FilterUnits;
}

export interface TextFilterFormGroup {
  mode: FormControl<FilterModes>;
  value: FormControl<string | null>;
}

export interface NumberFilterFormGroup {
  mode: FormControl<NumberFilterModes>;
  value: UntypedFormGroup;
}

export interface NumberFilterIntervalFormGroup {
  method: FormControl<PeriodFilterMethods>;
  number1: FormControl<number | null>;
  number2: FormControl<number | null>;
}

export interface NumberFilterRelativeFormGroup {
  method: FormControl<NumericRelativeFilterMethods>;
  number: FormControl<number | null>;
}

export interface SelectFilterFormGroup {
  mode: FormControl<FilterModes>;
  method: FormControl<SelectFilterMethods>;
  value: FormControl<Array<string | number>>;
}

export interface DateFilterFormGroup {
  mode: FormControl<DateFilterModes>;
  value:
    | FormGroup<DateFilterAbsoluteFormGroup>
    | FormGroup<DateFilterPeriodFormGroup>
    | FormGroup<DateFilterRelativeFormGroup>;
}

export interface DateFilterFormValue {
  mode: DateFilterModes;
  value:
    DateFilterAbsoluteValue | DateFilterPeriodValue | DateFilterRelativeValue;
}

export interface DateFilterAbsoluteFormGroup {
  method: FormControl<AbsoluteFilterMethods>;
  value: FormControl<string | null>;
  time?: FormControl<string | null>;
  within?: FormGroup<{
    value: FormControl<number | null>;
    unit: FormControl<FilterUnits | null>;
  }>;
}

export interface DateFilterPeriodFormGroup {
  method: FormControl<PeriodFilterMethods>;
  value1: FormControl<string | null>;
  time1?: FormControl<string | null>;
  value2: FormControl<string | null>;
  time2?: FormControl<string | null>;
}

export interface DateFilterRelativeFormGroup {
  method: FormControl<RelativeFilterMethods>;
  value: FormControl<number | null>;
  unit: FormControl<FilterUnits | null>;
}

export interface FilterMode {
  name: string;
  value: string;
}

export type TableFilter =
  TextFilter | NumberFilter | SelectFilter | DateFilter | AmountOfTimeFilter;

/**
 * The set of filter-type discriminants the table can render/serialize.
 *
 * Single source of truth — derived from the `TableFilter` union so a new filter
 * type only has to be added to `TableFilter` itself.
 */
export type TableFilterType = TableFilter['type'];

/** Column name → its filter type (or `undefined` when the column has no filter). */
export type TableFilterTypeMap = Map<string, TableFilterType | undefined>;
export type TableFilterValue =
  TextFilterValue | NumberFilterValue | DateFilterValue | SelectFilterValue;

/**
 * A filter value as it is sent to / received from the backend: the column's
 * filter type is merged into the value so it is self-describing.
 *
 * Only used at the HTTP boundary — client-side filter state holds plain
 * `TableFilterValue`s (see `filter-serializer.ts`).
 */
export type TypedTableFilterValue = TableFilterValue & {
  type?: TableFilterType;
};
export type TableFilterFormGroup =
  | TextFilterFormGroup
  | NumberFilterFormGroup
  | DateFilterFormGroup
  | SelectFilterFormGroup;

export const ONE_MONTH_IN_DAYS = 30;
export const ONE_YEAR_IN_DAYS = 365;

export const FILTER_MODES = ['include', 'exclude', 'empty'] as const;
export type FilterModes = (typeof FILTER_MODES)[number];

export const NUMBER_FILTER_MODES = ['relative', 'range'] as const;
export type NumberFilterModes = (typeof NUMBER_FILTER_MODES)[number];

export const DATE_FILTER_MODES = ['absolute', 'period', 'relative'] as const;
export type DateFilterModes = (typeof DATE_FILTER_MODES)[number];

export const TIME_FILTER_MODES = ['absolute', 'period'] as const;

export const DATE_FILTER_UNITS = ['days', 'weeks', 'months', 'years'] as const;

export const TIME_FILTER_UNITS = ['minutes', 'hours'] as const;

export const DATETIME_FILTER_UNITS = [
  ...TIME_FILTER_UNITS,
  ...DATE_FILTER_UNITS,
] as const;

export type FilterUnits = (typeof DATETIME_FILTER_UNITS)[number];

export const SELECT_FILTER_METHODS = ['all', 'any'] as const;
export type SelectFilterMethods = (typeof SELECT_FILTER_METHODS)[number];

export const NUMBER_FILTER_RELATIVE_METHODS = [
  'e',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
] as const;

export type NumericRelativeFilterMethods =
  (typeof NUMBER_FILTER_RELATIVE_METHODS)[number];

export const DATE_FILTER_RELATIVE_METHODS = ['last', 'past'] as const;

export type RelativeFilterMethods =
  (typeof DATE_FILTER_RELATIVE_METHODS)[number];

export const PERIOD_METHODS = ['between', 'not_between'] as const;

export type PeriodFilterMethods = (typeof PERIOD_METHODS)[number];

export const TIME_FILTER_ABSOLUTE_METHODS = [
  'at',
  'not_at',
  'before',
  'after',
] as const;

export type TimeFilterAbsoluteMethods =
  (typeof TIME_FILTER_ABSOLUTE_METHODS)[number];

export const DATE_FILTER_WITHIN_METHODS = [
  'before_within',
  'after_within',
] as const;

export type DateFilterWithinMethods =
  (typeof DATE_FILTER_WITHIN_METHODS)[number];

export const DATE_FILTER_ABSOLUTE_METHODS = [
  ...TIME_FILTER_ABSOLUTE_METHODS,
  ...DATE_FILTER_WITHIN_METHODS,
] as const;

export type AbsoluteFilterMethods =
  (typeof DATE_FILTER_ABSOLUTE_METHODS)[number];
